'use client';

import { Input } from '@/components/ui/input';
import { Info } from 'lucide-react';
import {
  RELIEF_APPROACH_HELP,
  RELIEF_APPROACH_LABEL,
  RELIEF_APPROACH_OPTIONS,
  type FormState,
  type ReliefApproach,
} from './coverageTypes';

/**
 * STEP 3 - THE RULES (PR 3, S6).
 *
 * Three percentages stood side by side in identical boxes and meant nothing alike: one is a
 * SERVICE-LEVEL TARGET and two are BATTERY THRESHOLDS. Identical styling made them read as one
 * family of settings, and the relationship an operator actually gets wrong - critical must be
 * below the landing floor - was enforced by a refusal after the fact rather than stated.
 *
 * SO THE TWO BATTERY NUMBERS ARE A SENTENCE and the target is its own question. A sentence states
 * the relationship in the reading, which is the only place it can be understood rather than
 * merely obeyed.
 *
 * THE PROSE AROUND THEM WAS THEN CUT ROUGHLY IN HALF, because this step no longer renders in a
 * 720 px dialog - it is in the COP's right column, 320 px by default. Every CLAIM survived; what
 * went was restatement of the sentence directly above it ("before it has to come home", "if the
 * watch falls under it"), a definition of the word report ("shown when you read the achieved
 * figure"), and a description of the mechanism nobody acts on ("the platform prepares the relief
 * and asks"). The eight load-bearing sentences are each pinned by name in
 * `CoverageAttachWizard.test.tsx`, which is what made a safe cut possible: exactly one of those
 * strings moved, and it kept its claim.
 *
 * HANDOVER AND CRITICAL POLICY ARE STATED, NOT OFFERED, and that is the important decision in this
 * file. The UX review specified two radio pairs - PROPOSE / FULL AUTO and ALERT ONLY / AUTO RTH -
 * and three of those four options name behaviour this platform does not perform:
 *
 *   - `attachCoverage` REFUSES `full_auto` outright unless `COVERAGE_SHADOW_FULL_AUTO` is set
 *     ("FULL AUTO handover is not available yet - it unlocks when the four-hour unattended
 *     acceptance run passes"), and even where it is stored no launch decision reads it: the
 *     orchestrator's approval requirement is unconditional.
 *   - `auto_rth` commands nothing. `coverage-safety.ts` says so in its own header and prints it in
 *     the alert: "This mission is configured for AUTO RTH, but the rotation does not command
 *     aircraft yet - no return has been sent."
 *
 * A live FULL AUTO control would be a button whose only outcome is an error toast, which is the
 * exact thing this panel's pre-press refusals exist to prevent. An AUTO RTH control would be
 * worse: it would let an operator attach an unattended multi-hour rotation believing a return is
 * armed. `coverage-safety.ts`'s own words - "a configured automatic action that silently does not
 * happen is worse than one that was never offered."
 *
 * RELIEF APPROACH IS OFFERED, AND IT IS THE SAME RULE REACHING THE OPPOSITE ANSWER (044). All
 * three of its values are honoured by `commandLaunch` the moment they are stored: `full_route` is
 * what every relief flies today, `station_only` is the shape the post-handover descent has
 * commanded since #540, and `outgoing_position` degrades to `station_only` in the two cases where
 * it cannot be flown rather than failing silently. Nothing here names behaviour no code path
 * performs, so the test above is passed and the control is real.
 *
 * ITS HELP TEXT STATES THE DEGRADATION rather than hiding it, for the reason the two bullets at
 * the foot of this step exist: an operator choosing "go to the aircraft on station" is owed the
 * knowledge that a drifted or silent aircraft sends the relief to the planned point instead -
 * before they choose, not after they watch it happen.
 */

export interface CoverageRulesStepProps {
  uid: string;
  coverageTargetPct: string;
  batteryNormalPct: string;
  batteryCriticalPct: string;
  reliefApproach: ReliefApproach;
  /**
   * How many aircraft are in the rotation right now, which the swap-approach group needs and
   * nothing else on this step does.
   *
   * A ROTATION OF ONE IS A SUPPORTED SHAPE (#564), not a half-filled form: one airframe lands,
   * swaps its pack and relaunches to the station it just left. It changes what one of the three
   * options can mean - there is no other aircraft to meet - and the step has to say so rather
   * than let an operator configure a mode that will never execute.
   *
   * Read from the live form and not from a submitted value, so removing the second aircraft on the
   * previous tab changes what this one says.
   */
  memberCount: number;
  onChange: (patch: Partial<FormState>) => void;
}

export function CoverageRulesStep({
  uid, coverageTargetPct, batteryNormalPct, batteryCriticalPct, reliefApproach, memberCount,
  onChange,
}: CoverageRulesStepProps) {
  /**
   * Exactly one aircraft picked - not zero, which is an unfinished form the attach refuses on its
   * own and which must not be described to the operator as a rotation of one.
   */
  const selfSwapRotation = memberCount === 1;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">
          Battery policy
        </div>
        {/* ONE SENTENCE, TWO NUMBERS, AND THE RELATIONSHIP BETWEEN THEM IN THE READING. As two
            labelled boxes the operator had to already know that critical sits below the landing
            floor; the refusal only told them once they had got it wrong. */}
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {/* "BRING HOME AT", NOT "LAND AT". The planner schedules the recall against this figure
              as a reserve floor (`reserveBatteryPercent`), so the aircraft STARTS returning here
              and touches down below it. The helper line under this sentence already said "before
              it has to come home"; the imperative above it disagreed. */}
          <span>Bring the aircraft home at</span>
          <Input
            id={`${uid}-normal`}
            // MATCHES THE VISIBLE WORDING (WCAG 2.5.3). The sentence around this input reads
            // "Bring the aircraft home at __ %", and the accessible name said "Land at %" - a
            // different phrase for the same control, which is what 2.5.3 is about, and the wrong
            // one of the two per this file's own note above.
            aria-label="Bring the aircraft home at %"
            // The two notes below the sentence carry the whole of what this number means,
            // including that the platform may raise it. Without this a screen-reader user tabbing
            // the form hears only "spin button, 20".
            aria-describedby={`${uid}-normal-help ${uid}-normal-raise`}
            type="number"
            min={1}
            max={100}
            value={batteryNormalPct}
            onChange={(e) => onChange({ batteryNormalPct: e.target.value })}
            className="h-7 w-16 text-xs font-mono tabular-nums"
          />
          {/* AT OR BELOW: `coverage-safety.ts` raises on `usable <= criticalPct`, so the alert
              fires exactly AT the configured value. The bare label this replaced made no claim
              precise enough to be wrong; a sentence does. */}
          <span>%, and raise a CRITICAL alert at or below</span>
          <Input
            id={`${uid}-critical`}
            aria-label="Critical %"
            type="number"
            min={1}
            max={100}
            value={batteryCriticalPct}
            onChange={(e) => onChange({ batteryCriticalPct: e.target.value })}
            className="h-7 w-16 text-xs font-mono tabular-nums"
          />
          <span>%.</span>
        </p>
        {/* THE LANDING FLOOR IS THE NUMBER THAT DRIVES EVERYTHING ELSE. It sets how much of the
            pack a sortie may spend, so it decides the on-station time on step 2 and the shape of
            the whole rotation - said here because nothing else names it.
            "before it has to come home" was cut as a restatement of the sentence directly above,
            and the consequence it was missing put in its place. */}
        <p id={`${uid}-normal-help`} className="text-[10px] text-muted-foreground mt-1.5">
          The home figure is the share of the pack each sortie may spend, so it sets the on-station
          time.
        </p>
        {/* A FLOOR, NOT A SETTING, SINCE #703 - and an operator is owed that before they type a
            number rather than after they watch it be ignored. The planner raises this figure to
            clear the percentage at which the aircraft's OWN low-battery RTH would take over,
            because once that fires it holds the flight controller: our return wayline is refused
            after dispatch and the sortie ends on a GPS-home landing with no tray and no tag
            (prod, 2026-09-09). Deliberately NOT a validation refusal - see D21. A number the
            platform will honour whenever it is the higher one is a real preference, so it is
            accepted and explained rather than rejected. The rotation panel shows both figures
            whenever they differ. */}
        <p id={`${uid}-normal-raise`} className="text-[10px] text-muted-foreground mt-1">
          The home figure is a floor. If this aircraft&apos;s own low-battery RTH would fire before
          it, the platform uses the higher of the two so the landing stays on the tray, so a
          rotation may be planned around a higher figure than you type. Today that floor is 25%.
        </p>
      </div>

      {/* WHERE THE SWAP GOES, placed directly under the battery policy because that policy decides
          WHEN a swap happens and this decides what it flies when it does - two halves of one
          sortie, and reading them apart is how an operator sets a 20% landing floor and then
          spends seven minutes of the relief's transit re-flying an approach leg.

          A RADIO GROUP AND NOT A SELECT. Three options whose whole content is the trade-off
          between them; a collapsed control shows one and hides the two it is being weighed
          against. The house pattern is `TriggerDispatchSection`'s, narrowed for the 320 px
          column. */}
      <fieldset>
        <legend className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">
          Swap approach
        </legend>
        {/* THE SENTENCE THE THREE OPTIONS COMPLETE. Without it the labels are three imperatives
            with no subject, and the subject is the part an operator has to get right: this is the
            ARRIVING aircraft's inbound leg, not the mission's route and not the outgoing
            aircraft's way home.

            THE SUBJECT IS THE LAUNCH, NOT THE PAIR, and that is a correction rather than a
            preference. "When one aircraft relieves another" excluded the rotation of ONE, which
            reads the sentence, concludes the control is about somebody else's setup, and leaves
            the default - and a rotation of one is the shape where this matters MOST, because its
            station is uncovered for the whole of the approach rather than held by an incumbent. */}
        <p id={`${uid}-approach-help`} className="text-[11px] text-muted-foreground mb-2">
          When an aircraft launches to take over the station, it flies:
        </p>
        <div className="space-y-1.5">
          {RELIEF_APPROACH_OPTIONS.map((value) => (
            <label
              key={value}
              className="flex gap-2 items-start rounded-md border border-border p-2 cursor-pointer
                has-[:checked]:border-primary"
            >
              <input
                type="radio"
                // UID-SCOPED, because this wizard mounts on the mission page AND inside the COP's
                // modal. A bare name would make two mounted copies one radio group, and choosing
                // in the modal would silently move the form behind it.
                name={`${uid}-relief-approach`}
                value={value}
                checked={reliefApproach === value}
                onChange={() => onChange({ reliefApproach: value })}
                aria-describedby={`${uid}-approach-help ${uid}-approach-${value}`}
                className="mt-0.5 shrink-0"
              />
              <span className="space-y-0.5">
                <span className="block text-xs text-foreground">
                  {RELIEF_APPROACH_LABEL[value]}
                </span>
                <span
                  id={`${uid}-approach-${value}`}
                  className="block text-[10px] text-muted-foreground"
                >
                  {RELIEF_APPROACH_HELP[value]}
                  {/* THE ONE OPTION THAT CANNOT FLY ON THIS ROTATION, said at the moment of
                      choosing. A rotation of one has no other aircraft to meet - the row's only
                      airframe IS the one relaunching - so the orchestrator resolves this mode to
                      the station point and the aircraft never goes looking for anybody.

                      SAID, NOT DISABLED. Disabling would be the stronger signal and the wrong
                      mechanism here: the roster lives on the PREVIOUS tab, so a disabled control
                      would flip under an operator who adds a second airframe, and a choice already
                      made would have to be silently rewritten to something they did not pick. A
                      sentence is true at every roster size and survives the roster changing.

                      It rides inside the option's own description node so `aria-describedby`
                      carries it without a second association. */}
                  {value === 'outgoing_position' && selfSwapRotation && (
                    <>
                      {' '}
                      <span className="text-foreground">
                        This rotation has one aircraft, so there is nobody to meet: it will fly
                        straight to the station.
                      </span>
                    </>
                  )}
                </span>
              </span>
            </label>
          ))}
        </div>
        {/* THE ONE CLAIM NONE OF THE THREE OPTIONS CAN CARRY, because it is true of all of them and
            is the misreading the whole control invites: a shorter approach is a shorter way to the
            station, not a lower one. The relief still climbs to its separation slot above the
            aircraft it is relieving, and the planned route is still what the map draws. */}
        <p className="text-[10px] text-muted-foreground mt-1.5">
          This changes the route only. Every choice holds the same station altitude, and the
          mission&apos;s own route is left exactly as you planned it.
        </p>
      </fieldset>

      <div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">
          Pass mark
        </div>
        {/* WHAT THIS NUMBER ACTUALLY DOES, and nothing more.
            It is not a geometry setting - standing beside two battery numbers under the label
            "Coverage %" it read as a fourth battery figure. But it is not a warning either:
            `coverage_target_pct` has exactly one consumer in the whole platform,
            `coverage-achieved.ts`'s `meets_target`, which becomes the PASS/FAIL on the achieved
            read. Nothing raises an alert from it, and that read is on demand. "Warn me if achieved
            coverage falls under X" would be the AUTO RTH mistake in a new place: a configured
            automatic action that silently does not happen. */}
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span>Mark this watch FAILED if it ends under</span>
          <Input
            id={`${uid}-pct`}
            aria-label="Coverage %"
            type="number"
            min={1}
            max={100}
            value={coverageTargetPct}
            onChange={(e) => onChange({ coverageTargetPct: e.target.value })}
            className="h-7 w-16 text-xs font-mono tabular-nums"
          />
          <span>% of the window covered.</span>
        </p>
        {/* Both claims, no restatement: it is a GRADE (not a trigger), and nothing reaches the
            operator on its own. "shown when you read the achieved figure" is what a report is, and
            "if the watch falls under it" is what the sentence above already said. */}
        <p className="text-[10px] text-muted-foreground mt-1.5">
          A grade on this mission&apos;s coverage report. Nothing is pushed to you.
        </p>
      </div>

      {/* STATED, NOT OFFERED. See the header - three of the four options the review specified name
          behaviour no code path performs, and the server refuses one of them outright. */}
      <div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">
          Not automatic
        </div>
        <ul className="space-y-1.5 text-[11px] text-muted-foreground">
          <li className="flex items-start gap-1.5">
            <Info className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            <span>
              <span className="text-foreground">Every swap waits for your approval.</span>
              {' '}FULL AUTO is not available yet; it unlocks when the four-hour unattended
              acceptance run passes.
            </span>
          </li>
          {/* SCOPED TO THE CRITICAL THRESHOLD, and deliberately says nothing about automation in
              general - because the general claim is not true on every deployment. The rotation DOES
              command aircraft where `COVERAGE_ORCHESTRATOR_ENABLED` is on: it launches the relief
              and recalls the outgoing airframe at the very landing figure set above. This panel's
              action card already branches that sentence on `automatic_launch_enabled`, and its
              comment records why a flat under-promise is worse than an over-promise - it tells
              somebody to act. What IS true on every deployment is that nothing is commanded in
              response to the critical crossing, which is what this bullet is about. */}
          <li className="flex items-start gap-1.5">
            <Info className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            <span>
              <span className="text-foreground">Crossing the critical level raises an alert, and
                nothing else.
              </span>
              {' '}No return is commanded in response to it, on any deployment - the aircraft is
              yours to recover.
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
